import { SUCCESS_MESSAGES } from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Box, Stack } from '@zorro/web/design-system';
import {
  showErrorNotification,
  showSuccessNotification,
  useLoadingOverlay,
} from '@zorro/web/ui-utils';
import { useEffect, useMemo, useState } from 'react';

import { AllowanceModelWorkflowProgressModal } from '../AllowanceModelWorkflowProgressModal';
import { FormFooter } from '../FormFooter';
import { OepUpdateWorkflowProgressModal } from '../OepUpdateWorkflowProgressModal';
import { PlanYearSetupFormInputs } from './PlanYearSetupFormInputs';
import { usePlanYearSetupForm } from './usePlanYearSetupForm';

const isFinalizationMode = true;

type Props = {
  employerId: string;
  onSuccess: () => void;
  isEdit: boolean;
  openEnrollmentPeriodId?: string;
};

export const PlanYearSetupForm = ({
  employerId,
  isEdit,
  openEnrollmentPeriodId,
  onSuccess,
}: Props) => {
  const { startLoading, stopLoading } = useLoadingOverlay();
  const {
    planYearSetupForm,
    isAllowanceModelRequired,
    isExistingModelRemoved,
    allowanceModelSourceNote,
    allowanceModelSourceLabel,
    existingAllowanceModel,
    planYearUpdateErrors,
    onSubmit,
  } = usePlanYearSetupForm({
    selectedOpenEnrollmentPeriodId: openEnrollmentPeriodId,
    isFinalizationMode,
    employerId,
  });
  const [oepWorkflowCorrelationId, setOepWorkflowCorrelationId] = useState<
    string | null
  >(null);
  const [
    allowanceModelWorkflowCorrelationId,
    setAllowanceModelWorkflowCorrelationId,
  ] = useState<string | null>(null);
  const [hasPendingWorkflows, setHasPendingWorkflows] = useState(false);

  const { isValid, isDirty } = planYearSetupForm.formState || {};
  const hasChanges = isDirty || isExistingModelRemoved;
  const isSubmitDisabled = isEdit ? !isValid || !hasChanges : !isValid;

  const handleSubmit = async () => {
    startLoading();
    let result: {
      success: boolean;
      oepCorrelationId?: string | null;
      allowanceModelCorrelationId?: string | null;
    };
    try {
      result = await onSubmit();
    } finally {
      stopLoading();
    }

    const { success, oepCorrelationId, allowanceModelCorrelationId } = result;
    if (!success) return;

    if (oepCorrelationId) {
      setOepWorkflowCorrelationId(oepCorrelationId);
    }

    if (allowanceModelCorrelationId) {
      setAllowanceModelWorkflowCorrelationId(allowanceModelCorrelationId);
    }

    if (oepCorrelationId || allowanceModelCorrelationId) {
      setHasPendingWorkflows(true);
    } else {
      // Only show success and call onSuccess if no workflows are running
      showSuccessNotification({
        message: SUCCESS_MESSAGES.SAVED_SUCCESSFULLY_MESSAGE,
      });
      onSuccess();
    }
  };

  const handleOepWorkflowComplete = () => {
    setOepWorkflowCorrelationId(null);
  };

  const handleOepWorkflowError = () => {
    setOepWorkflowCorrelationId(null);
    setHasPendingWorkflows(false);
    showErrorNotification({
      message:
        'We could not finish updating onboarding periods. Please try again.',
    });
  };

  const handleAllowanceModelWorkflowComplete = () => {
    setAllowanceModelWorkflowCorrelationId(null);
  };

  const handleAllowanceModelWorkflowError = () => {
    setAllowanceModelWorkflowCorrelationId(null);
    setHasPendingWorkflows(false);
    showErrorNotification({
      message:
        'We could not finish updating employee classes. Please try again.',
    });
  };

  useEffect(() => {
    if (
      hasPendingWorkflows &&
      !oepWorkflowCorrelationId &&
      !allowanceModelWorkflowCorrelationId
    ) {
      setHasPendingWorkflows(false);
      showSuccessNotification({
        message: SUCCESS_MESSAGES.SAVED_SUCCESSFULLY_MESSAGE,
      });
      onSuccess();
    }
  }, [
    hasPendingWorkflows,
    oepWorkflowCorrelationId,
    allowanceModelWorkflowCorrelationId,
    onSuccess,
  ]);

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          variant: 'tertiary',
          onClick: onSuccess,
        }}
        primaryLabel="Submit"
        primaryButtonProps={{
          disabled: isSubmitDisabled,
          onClick: handleSubmit,
        }}
        paperProps={{
          pos: 'sticky',
          bottom: 0,
        }}
      />
    ),
    [onSuccess, isSubmitDisabled, handleSubmit]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack h="100%">
      <Box px="xxl" pb="lg">
        <PlanYearSetupFormInputs
          planYearSetupForm={planYearSetupForm}
          isFinalizationMode
          isAllowanceModelRequired={isAllowanceModelRequired}
          planYearUpdateErrors={planYearUpdateErrors}
          employerId={employerId}
          allowanceModelSourceNote={allowanceModelSourceNote}
          allowanceModelSourceLabel={allowanceModelSourceLabel}
          existingAllowanceModel={existingAllowanceModel}
        />
      </Box>
      <OepUpdateWorkflowProgressModal
        correlationId={oepWorkflowCorrelationId}
        onComplete={handleOepWorkflowComplete}
        onError={handleOepWorkflowError}
        onClose={() => setOepWorkflowCorrelationId(null)}
      />
      <AllowanceModelWorkflowProgressModal
        correlationId={allowanceModelWorkflowCorrelationId}
        onComplete={handleAllowanceModelWorkflowComplete}
        onError={handleAllowanceModelWorkflowError}
        onClose={() => setAllowanceModelWorkflowCorrelationId(null)}
      />
    </Stack>
  );
};
